import React, { useState, useEffect, createElement } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Switch, TextInput, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AdminEmailDropdown from './AdminEmailDropdown';

/**
 * Notification Preferences Tab
 * 
 * Allows admins to configure:
 * - Health report email schedule (8am/8pm)
 * - Email recipients (from admin users)
 * - What to include in reports
 * - Send test reports
 */
const NotificationPreferencesTab = () => {
  const [preferences, setPreferences] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  /** Typed email for “add recipient” only (health report CC list). */
  const [manualRecipientEmail, setManualRecipientEmail] = useState('');
  /** Admin picked for health report test send. */
  const [healthReportTestTo, setHealthReportTestTo] = useState('');
  const [signupWelcomeDraft, setSignupWelcomeDraft] = useState({
    subject: '',
    eyebrow: '',
    headline: '',
    intro: '',
    bulletsText: '',
    ctaLabel: '',
    ctaUrl: '',
    closingLine: '',
  });
  const [signupPreviewHtml, setSignupPreviewHtml] = useState('');
  const [signupTestTo, setSignupTestTo] = useState('');
  const [sendingSignupTest, setSendingSignupTest] = useState(false);
  const [loadingSignupPreview, setLoadingSignupPreview] = useState(false);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Load preferences
  useEffect(() => {
    fetchPreferences();
  }, []);
  
  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(`${functionsUrl}/getNotificationPreferences`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      if (data.success) {
        setPreferences(data.preferences);
        setAdminUsers(data.adminUsers || []);
        
        const defs = data.signupWelcomeDefaults || {};
        const saved = data.preferences?.transactionalEmails?.signupWelcome || {};
        const bulletList =
          Array.isArray(saved.bullets) && saved.bullets.length
            ? saved.bullets
            : defs.bullets || [];
        setSignupWelcomeDraft({
          subject: saved.subject ?? defs.subject ?? '',
          eyebrow: saved.eyebrow ?? defs.eyebrow ?? '',
          headline: saved.headline ?? defs.headline ?? '',
          intro: saved.intro ?? defs.intro ?? '',
          bulletsText: bulletList.join('\n'),
          ctaLabel: saved.ctaLabel ?? defs.ctaLabel ?? '',
          ctaUrl: saved.ctaUrl ?? defs.ctaUrl ?? '',
          closingLine: saved.closingLine ?? defs.closingLine ?? '',
        });
        setSignupPreviewHtml('');

        const admins = data.adminUsers || [];
        const adminSet = new Set(admins.map((u) => u.email));
        const firstAdminEmail = admins[0]?.email || '';
        const preferredHealth =
          data.preferences?.healthReports?.recipients?.find((e) => adminSet.has(e)) ||
          firstAdminEmail;
        setHealthReportTestTo(preferredHealth || '');
        setSignupTestTo(firstAdminEmail);
        setManualRecipientEmail('');
      }
    } catch (err) {
      console.error('[NotificationPrefs] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const savePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const transactionalEmails = {
        ...(preferences.transactionalEmails || {}),
        signupWelcome: {
          subject: (signupWelcomeDraft.subject || '').trim(),
          eyebrow: (signupWelcomeDraft.eyebrow || '').trim(),
          headline: (signupWelcomeDraft.headline || '').trim(),
          intro: (signupWelcomeDraft.intro || '').trim(),
          bullets: (signupWelcomeDraft.bulletsText || '')
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
          ctaLabel: (signupWelcomeDraft.ctaLabel || '').trim(),
          ctaUrl: (signupWelcomeDraft.ctaUrl || '').trim() || undefined,
          closingLine: (signupWelcomeDraft.closingLine || '').trim(),
        },
      };

      const res = await fetch(`${functionsUrl}/updateNotificationPreferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferences: { ...preferences, transactionalEmails },
        })
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      if (data.success) {
        setPreferences((prev) =>
          prev ? { ...prev, transactionalEmails } : prev
        );
        setSuccessMessage('Preferences saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('[NotificationPrefs] Save error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const sendTestReport = async () => {
    const to = (healthReportTestTo || '').trim();
    if (!to) {
      setError('Select an admin to send the test report');
      return;
    }
    
    try {
      setSendingTest(true);
      setError(null);
      setSuccessMessage(null);
      
      const res = await fetch(`${functionsUrl}/sendHealthReport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testEmail: to })
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      if (data.success) {
        setSuccessMessage(`Test report sent to ${to}!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to send test report');
      }
    } catch (err) {
      console.error('[NotificationPrefs] Test send error:', err);
      setError(err.message);
    } finally {
      setSendingTest(false);
    }
  };

  const patchSignupDraft = (key, value) => {
    setSignupWelcomeDraft((d) => ({ ...d, [key]: value }));
  };

  const refreshSignupPreview = async () => {
    try {
      setLoadingSignupPreview(true);
      setError(null);
      const res = await fetch(`${functionsUrl}/previewSignupWelcomeEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signupWelcome: {
            ...signupWelcomeDraft,
            bulletsText: signupWelcomeDraft.bulletsText,
          },
          previewDisplayName: 'Jordan',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success && data.html) {
        setSignupPreviewHtml(data.html);
      } else {
        throw new Error(data.error || 'Preview failed');
      }
    } catch (err) {
      console.error('[NotificationPrefs] signup preview:', err);
      setError(err.message);
    } finally {
      setLoadingSignupPreview(false);
    }
  };

  const sendSignupTest = async () => {
    const to = (signupTestTo || '').trim();
    if (!to) {
      setError('Choose an admin email for the test send');
      return;
    }
    try {
      setSendingSignupTest(true);
      setError(null);
      setSuccessMessage(null);
      const res = await fetch(`${functionsUrl}/sendTestSignupWelcomeEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          signupWelcome: {
            ...signupWelcomeDraft,
            bulletsText: signupWelcomeDraft.bulletsText,
          },
        }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.success) {
        setSuccessMessage(data.message || `Test signup welcome sent to ${to}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingSignupTest(false);
    }
  };
  
  const toggleRecipient = (email) => {
    if (!preferences) return;
    
    const recipients = preferences.healthReports?.recipients || [];
    const newRecipients = recipients.includes(email)
      ? recipients.filter(e => e !== email)
      : [...recipients, email];
    
    setPreferences({
      ...preferences,
      healthReports: {
        ...preferences.healthReports,
        recipients: newRecipients
      }
    });
  };
  
  const toggleSchedule = (index) => {
    if (!preferences) return;
    
    const schedule = [...(preferences.healthReports?.schedule || [])];
    schedule[index] = { ...schedule[index], enabled: !schedule[index].enabled };
    
    setPreferences({
      ...preferences,
      healthReports: {
        ...preferences.healthReports,
        schedule
      }
    });
  };
  
  const toggleIncludeOption = (key) => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      includeInReport: {
        ...preferences.includeInReport,
        [key]: !preferences.includeInReport?.[key]
      }
    });
  };
  
  const toggleHealthReports = () => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      healthReports: {
        ...preferences.healthReports,
        enabled: !preferences.healthReports?.enabled
      }
    });
  };
  
  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={styles.loadingText}>Loading preferences...</Text>
        </View>
      </View>
    );
  }
  
  return (
    <ScrollView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Health Report Settings</Text>
        <Text style={styles.subtitle}>Configure automated health report emails</Text>
      </View>
      
      {/* Success/Error Messages */}
      {successMessage && (
        <View style={styles.successBanner}>
          <Ionicons name="checkmark-circle" size={20} color="#10B981" />
          <Text style={styles.successText}>{successMessage}</Text>
        </View>
      )}
      
      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="alert-circle" size={20} color="#EF4444" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={() => setError(null)}>
            <Ionicons name="close" size={20} color="#EF4444" />
          </TouchableOpacity>
        </View>
      )}
      
      {/* Enable/Disable Card */}
      <View style={styles.card}>
        <View style={styles.toggleRow}>
          <View style={styles.toggleInfo}>
            <Text style={styles.toggleLabel}>Enable Health Reports</Text>
            <Text style={styles.toggleDescription}>
              Receive automated daily health report emails
            </Text>
          </View>
          <Switch
            value={preferences?.healthReports?.enabled ?? true}
            onValueChange={toggleHealthReports}
            trackColor={{ false: '#374151', true: '#8B5CF640' }}
            thumbColor={preferences?.healthReports?.enabled ? '#8B5CF6' : '#9CA3AF'}
          />
        </View>
      </View>
      
      {/* Schedule Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Email Schedule</Text>
        <Text style={styles.cardDescription}>
          Choose when to receive health reports (times shown in PST)
        </Text>
        
        <View style={styles.scheduleList}>
          {(preferences?.healthReports?.schedule || [
            { time: '08:00', timezone: 'America/Los_Angeles', enabled: true },
            { time: '20:00', timezone: 'America/Los_Angeles', enabled: true }
          ]).map((item, index) => (
            <TouchableOpacity
              key={index}
              style={[styles.scheduleItem, item.enabled && styles.scheduleItemActive]}
              onPress={() => toggleSchedule(index)}
            >
              <View style={styles.scheduleIcon}>
                <Ionicons 
                  name={item.time === '08:00' ? 'sunny' : 'moon'} 
                  size={24} 
                  color={item.enabled ? '#8B5CF6' : '#6B7280'} 
                />
              </View>
              <View style={styles.scheduleInfo}>
                <Text style={[styles.scheduleTime, item.enabled && styles.scheduleTimeActive]}>
                  {item.time === '08:00' ? '8:00 AM' : '8:00 PM'}
                </Text>
                <Text style={styles.scheduleLabel}>
                  {item.time === '08:00' ? 'Morning Report' : 'Evening Report'}
                </Text>
              </View>
              <View style={[styles.scheduleCheck, item.enabled && styles.scheduleCheckActive]}>
                {item.enabled && <Ionicons name="checkmark" size={16} color="#FFFFFF" />}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      
      {/* Recipients Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Email Recipients</Text>
        <Text style={styles.cardDescription}>
          Select admin users who should receive health reports
        </Text>
        
        <View style={styles.recipientList}>
          {adminUsers.length === 0 ? (
            <Text style={styles.noRecipients}>No admin users found</Text>
          ) : (
            adminUsers.map((user) => {
              const isSelected = preferences?.healthReports?.recipients?.includes(user.email);
              return (
                <TouchableOpacity
                  key={user.id}
                  style={[styles.recipientItem, isSelected && styles.recipientItemSelected]}
                  onPress={() => toggleRecipient(user.email)}
                >
                  <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                    {isSelected && <Ionicons name="checkmark" size={14} color="#FFFFFF" />}
                  </View>
                  <View style={styles.recipientInfo}>
                    <Text style={styles.recipientEmail}>{user.email}</Text>
                    {user.displayName && user.displayName !== user.email && (
                      <Text style={styles.recipientName}>{user.displayName}</Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>
        
        {/* Add custom email */}
        <View style={styles.customEmailSection}>
          <Text style={styles.customEmailLabel}>Or add email manually:</Text>
          <View style={styles.customEmailRow}>
            <TextInput
              style={styles.customEmailInput}
              value={manualRecipientEmail}
              onChangeText={setManualRecipientEmail}
              placeholder="email@example.com"
              placeholderTextColor="#64748B"
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <TouchableOpacity
              style={[styles.addButton, !manualRecipientEmail && styles.addButtonDisabled]}
              onPress={() => {
                const e = manualRecipientEmail.trim();
                if (e && !preferences?.healthReports?.recipients?.includes(e)) {
                  toggleRecipient(e);
                  setManualRecipientEmail('');
                }
              }}
              disabled={!manualRecipientEmail?.trim()}
            >
              <Ionicons name="add" size={20} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
      
      {/* Report Contents Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Report Contents</Text>
        <Text style={styles.cardDescription}>
          Choose what sections to include in health reports
        </Text>
        
        <View style={styles.optionsList}>
          {[
            { key: 'cacheStatus', label: 'Cache Status', description: 'Fixtures, markets, and refresh timing' },
            { key: 'queryMetrics', label: 'Query Metrics', description: 'Search volume, success rate, top searches' },
            { key: 'userStats', label: 'User Statistics', description: 'Total users, new signups' },
            { key: 'coverageGaps', label: 'Coverage Gaps', description: 'Missing markets and sportsbooks' },
            { key: 'aiSummary', label: 'AI Summary', description: 'AI-generated executive summary' },
            { key: 'historicalComparison', label: 'Historical Comparison', description: 'Compare to yesterday\'s metrics' },
          ].map((option) => (
            <View key={option.key} style={styles.optionItem}>
              <View style={styles.optionInfo}>
                <Text style={styles.optionLabel}>{option.label}</Text>
                <Text style={styles.optionDescription}>{option.description}</Text>
              </View>
              <Switch
                value={preferences?.includeInReport?.[option.key] ?? true}
                onValueChange={() => toggleIncludeOption(option.key)}
                trackColor={{ false: '#374151', true: '#8B5CF640' }}
                thumbColor={preferences?.includeInReport?.[option.key] ? '#8B5CF6' : '#9CA3AF'}
              />
            </View>
          ))}
        </View>
      </View>

      {/* Self-signup welcome email */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Self-signup welcome email</Text>
        <Text style={styles.cardDescription}>
          Copy for the email sent when users register themselves (email/password or new Google account).
          Save applies to production sends; preview uses your current fields (including unsaved edits).
        </Text>

        {[
          { key: 'subject', label: 'Subject line', multiline: false },
          { key: 'eyebrow', label: 'Eyebrow (small caps above headline)', multiline: false },
          { key: 'headline', label: 'Headline', multiline: false },
          { key: 'intro', label: 'Intro (use a blank line between paragraphs)', multiline: true },
          { key: 'bulletsText', label: 'Bullets (one per line)', multiline: true },
          { key: 'ctaLabel', label: 'Button label', multiline: false },
          { key: 'ctaUrl', label: 'Button URL (optional — defaults to app URL)', multiline: false },
          { key: 'closingLine', label: 'Closing line', multiline: true },
        ].map((field) => (
          <View key={field.key} style={styles.signupFieldBlock}>
            <Text style={styles.signupFieldLabel}>{field.label}</Text>
            <TextInput
              style={field.multiline ? styles.signupMultilineInput : styles.signupLineInput}
              value={signupWelcomeDraft[field.key]}
              onChangeText={(t) => patchSignupDraft(field.key, t)}
              placeholderTextColor="#64748B"
              multiline={field.multiline}
              numberOfLines={field.multiline ? 4 : 1}
            />
          </View>
        ))}

        <Text style={styles.signupFieldLabel}>Preview</Text>
        <View style={styles.signupPreviewWrap}>
          {Platform.OS === 'web' && signupPreviewHtml
            ? createElement('iframe', {
                title: 'Signup welcome preview',
                srcDoc: signupPreviewHtml,
                style: {
                  width: '100%',
                  height: 440,
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: '#334155',
                  borderRadius: 8,
                  backgroundColor: '#0f0f17',
                },
                sandbox: 'allow-same-origin',
              })
            : (
              <Text style={styles.previewPlaceholder}>
                {Platform.OS === 'web'
                  ? 'Tap “Refresh preview” to render the HTML email.'
                  : 'HTML preview is available in the web admin.'}
              </Text>
            )}
        </View>

        <View style={styles.signupActionsRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, loadingSignupPreview && styles.testButtonDisabled]}
            onPress={refreshSignupPreview}
            disabled={loadingSignupPreview}
          >
            {loadingSignupPreview ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="eye" size={18} color="#FFFFFF" />
                <Text style={styles.secondaryButtonText}>Refresh preview</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <Text style={[styles.signupFieldLabel, { marginTop: 12 }]}>Test send (admin inboxes only)</Text>
        <AdminEmailDropdown
          adminUsers={adminUsers}
          value={signupTestTo}
          onChange={setSignupTestTo}
          placeholder="Select admin"
          disabled={sendingSignupTest}
        />
        <TouchableOpacity
          style={[styles.testButton, sendingSignupTest && styles.testButtonDisabled]}
          onPress={sendSignupTest}
          disabled={sendingSignupTest}
        >
          {sendingSignupTest ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="mail" size={18} color="#FFFFFF" />
              <Text style={styles.testButtonText}>Send test welcome</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Test Email Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Test Report</Text>
        <Text style={styles.cardDescription}>
          Send a test health report to preview the email format
        </Text>
        
        <View style={styles.testSection}>
          <Text style={styles.signupFieldLabel}>Send to</Text>
          <AdminEmailDropdown
            adminUsers={adminUsers}
            value={healthReportTestTo}
            onChange={setHealthReportTestTo}
            placeholder="Select admin"
            disabled={sendingTest}
          />
          <TouchableOpacity
            style={[styles.testButton, sendingTest && styles.testButtonDisabled]}
            onPress={sendTestReport}
            disabled={sendingTest}
          >
            {sendingTest ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="paper-plane" size={18} color="#FFFFFF" />
                <Text style={styles.testButtonText}>Send Test Report</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Save Button */}
      <View style={styles.saveSection}>
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.saveButtonDisabled]}
          onPress={savePreferences}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="save" size={20} color="#FFFFFF" />
              <Text style={styles.saveButtonText}>Save Preferences</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Info Box */}
      <View style={styles.infoBox}>
        <Ionicons name="information-circle" size={20} color="#8B5CF6" />
        <Text style={styles.infoText}>
          Health reports are sent automatically at the scheduled times. Reports include AI-generated 
          insights that analyze system performance and provide actionable recommendations.
        </Text>
      </View>
      
      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    color: '#94A3B8',
    fontSize: 16,
    marginTop: 16,
  },
  header: {
    padding: 20,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    color: '#94A3B8',
    fontSize: 14,
  },
  
  // Banners
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#10B98120',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#10B98140',
  },
  successText: {
    color: '#10B981',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#EF444420',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#EF444440',
  },
  errorText: {
    color: '#EF4444',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  
  // Cards
  card: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  cardDescription: {
    color: '#94A3B8',
    fontSize: 13,
    marginBottom: 16,
  },
  
  // Toggle Row
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleInfo: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  toggleDescription: {
    color: '#94A3B8',
    fontSize: 13,
  },
  
  // Schedule
  scheduleList: {
    gap: 12,
  },
  scheduleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0F172A',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#334155',
  },
  scheduleItemActive: {
    borderColor: '#8B5CF6',
    backgroundColor: '#8B5CF610',
  },
  scheduleIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#1E293B',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  scheduleInfo: {
    flex: 1,
  },
  scheduleTime: {
    color: '#94A3B8',
    fontSize: 18,
    fontWeight: '700',
  },
  scheduleTimeActive: {
    color: '#FFFFFF',
  },
  scheduleLabel: {
    color: '#64748B',
    fontSize: 13,
  },
  scheduleCheck: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#374151',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scheduleCheckActive: {
    backgroundColor: '#8B5CF6',
  },
  
  // Recipients
  recipientList: {
    gap: 8,
    marginBottom: 16,
  },
  recipientItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0F172A',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#334155',
  },
  recipientItemSelected: {
    borderColor: '#8B5CF6',
    backgroundColor: '#8B5CF610',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    backgroundColor: '#374151',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    borderWidth: 2,
    borderColor: '#4B5563',
  },
  checkboxSelected: {
    backgroundColor: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  recipientInfo: {
    flex: 1,
  },
  recipientEmail: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  recipientName: {
    color: '#64748B',
    fontSize: 12,
    marginTop: 2,
  },
  noRecipients: {
    color: '#64748B',
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },
  
  // Custom email
  customEmailSection: {
    borderTopWidth: 1,
    borderTopColor: '#334155',
    paddingTop: 16,
  },
  customEmailLabel: {
    color: '#94A3B8',
    fontSize: 13,
    marginBottom: 8,
  },
  customEmailRow: {
    flexDirection: 'row',
    gap: 8,
  },
  customEmailInput: {
    flex: 1,
    backgroundColor: '#0F172A',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#FFFFFF',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#334155',
  },
  addButton: {
    backgroundColor: '#8B5CF6',
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addButtonDisabled: {
    backgroundColor: '#4B5563',
  },
  
  // Options
  optionsList: {
    gap: 12,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  optionInfo: {
    flex: 1,
    marginRight: 16,
  },
  optionLabel: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 2,
  },
  optionDescription: {
    color: '#64748B',
    fontSize: 12,
  },
  
  // Test Section
  testSection: {
    gap: 12,
  },

  testButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#6366F1',
    paddingVertical: 14,
    borderRadius: 8,
  },
  testButtonDisabled: {
    backgroundColor: '#4B5563',
  },
  testButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },

  signupFieldBlock: {
    marginBottom: 12,
  },
  signupFieldLabel: {
    color: '#94A3B8',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
  },
  signupLineInput: {
    backgroundColor: '#0F172A',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#FFFFFF',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#334155',
  },
  signupMultilineInput: {
    backgroundColor: '#0F172A',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#FFFFFF',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#334155',
    minHeight: 88,
    textAlignVertical: 'top',
  },
  signupPreviewWrap: {
    minHeight: 120,
    marginBottom: 12,
    justifyContent: 'center',
  },
  previewPlaceholder: {
    color: '#64748B',
    fontSize: 13,
    paddingVertical: 24,
    textAlign: 'center',
  },
  signupActionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#475569',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  secondaryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Save Section
  saveSection: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#8B5CF6',
    paddingVertical: 16,
    borderRadius: 12,
  },
  saveButtonDisabled: {
    backgroundColor: '#4B5563',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  
  // Info Box
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: '#8B5CF610',
    marginHorizontal: 16,
    marginTop: 8,
    padding: 14,
    borderRadius: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#8B5CF6',
  },
  infoText: {
    color: '#CBD5E1',
    fontSize: 13,
    lineHeight: 20,
    flex: 1,
  },
});

export default NotificationPreferencesTab;
